import useOffer from 'hooks/Offers/useOffer'
import { shallowEqual, useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import { getBedbankItemViewTotals } from 'checkout/lib/utils/bedbank/view'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import { useUserCartItemState } from './useUserCartItemState'
import { useEffect, useMemo } from 'react'
import { userCartItemsSaveForLater, userCartItemUpdate } from 'actions/UserCartActions'
import { dateIsBeforeOrSame } from 'lib/datetime/dateUtils'
import { getUserCartAccommodationItems } from 'selectors/userCart'
import { isBedbankItem } from 'lib/checkout/checkoutUtils'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'
import { matchBedbankRateOrItem } from 'lib/hotels/bedbankUtils'

export type UserCartBedbankHotelItemData = {
  offer: App.BedbankOffer;
  loaded: true;
  loading: false;
  error: any;
  package: App.BedbankPackage;
  itemTotals: App.Checkout.ItemViewTotals;
  isPast: boolean;
  isSoldOut: boolean;
  isDeleted: boolean;
  isSavedForLater: boolean;
  isBookable: boolean;
  rate: App.BedbankRate | undefined;
} | {
  offer: App.BedbankOffer | undefined;
  loaded: false;
  loading: boolean;
  error: any;
  package: App.BedbankPackage | undefined;
  itemTotals: App.Checkout.ItemViewTotals | undefined;
  isPast: boolean;
  isSoldOut: boolean;
  isDeleted: boolean;
  isSavedForLater: boolean;
  isBookable: boolean;
  rate: App.BedbankRate | undefined;
}

function useUserCartBedbankHotelItemData(item: App.Checkout.BedbankHotelItem, saveForLaterIfUnavailable = true): UserCartBedbankHotelItemData {
  const dispatch = useAppDispatch()
  const { offerId, checkIn, checkOut, occupancy } = item
  const { deleted, saveForLater } = useUserCartItemState(item.itemId)
  const bedbankOfferRates = useAppSelector(state => state.offer.bedbankOfferRates)

  const [offer, offerLoading, offerError] = useOffer<App.BedbankOffer>(item.offerId)

  const itemStates = useAppSelector(state => state.auth.userCart.itemStates)

  const accommodationItems = useAppSelector(getUserCartAccommodationItems)

  let itemGroup: Array<App.Checkout.BedbankHotelItem>

  if (saveForLater) {
    itemGroup = [item]
  } else {
    itemGroup = accommodationItems
      .filter(isBedbankItem)
      .filter(bedbankItem => !itemStates[bedbankItem.itemId]?.deleted && !itemStates[bedbankItem.itemId]?.saveForLater)
      .filter(bedbankItem => item.offerId === offerId &&
        item.checkIn === checkIn &&
        item.checkOut === checkOut &&
        matchBedbankRateOrItem(item, bedbankItem),
      )
  }
  const rateKey = getBedbankRateKey(itemGroup.map(item => item.occupancy), checkIn, checkOut)
  const rate = useMemo<App.BedbankRate | undefined>(() => {
    return bedbankOfferRates[item.offerId]?.[rateKey]?.find(r => r.id === item.roomRateId && r.isFlightBundle === item.isFlightBundle)
  }, [bedbankOfferRates, item.offerId, item.roomRateId, item.isFlightBundle, rateKey])
  const {
    rates,
    ratesLoading,
    luxPlusLPPEnabled,
  } = useAppSelector(state => ({
    rates: state.offer.bedbankOfferRates[offerId]?.[rateKey],
    ratesLoading: state.offer.bedbankOfferRatesLoading[offerId]?.[rateKey],
    luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(state),
  }), shallowEqual)

  let bedbankOfferRate = rates?.find(rate => rate.id === item.roomRateId && rate.isFlightBundle === item.isFlightBundle)
  let matchedNewRateId = false
  if (!bedbankOfferRate) {
    bedbankOfferRate = rates?.find(rate => matchBedbankRateOrItem(item, rate))
    matchedNewRateId = !!bedbankOfferRate
  }
  const pkg = bedbankOfferRate?.room

  // Eslint is wrong. `ratesLoading` is false when the rates are loaded, and undefined when they haven't been loaded yet, and we need to make that distinction
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
  const isSoldOut = ratesLoading === false && !bedbankOfferRate
  const isPast = dateIsBeforeOrSame(new Date(checkIn), new Date())
  const isBookable = !isPast && !isSoldOut && !offerError

  useEffect(() => {
    if (!matchedNewRateId) return
    if (!bedbankOfferRate) return
    // Update item with the newly matched rate
    const updatedItem: App.Checkout.BedbankHotelItem = {
      ...item,
      roomRateId: bedbankOfferRate.id,
      rateGroupId: bedbankOfferRate.groupId,
      bedGroupId: bedbankOfferRate.bedGroups[0].id,
      isFlightBundle: bedbankOfferRate.isFlightBundle,
    }
    dispatch(userCartItemUpdate(updatedItem))
  }, [bedbankOfferRate, item, matchedNewRateId, dispatch])

  useEffect(() => {
    if (saveForLaterIfUnavailable && !isBookable && !saveForLater && !offerLoading && !ratesLoading && !deleted) {
      dispatch(userCartItemsSaveForLater([item.itemId]))
    }
  }, [isBookable, saveForLater, offerLoading, ratesLoading, dispatch, item.itemId, deleted, saveForLaterIfUnavailable])

  if (offer && pkg && bedbankOfferRate) {
    const itemTotals = getBedbankItemViewTotals(bedbankOfferRate, occupancy, luxPlusLPPEnabled)

    return {
      offer,
      loaded: true,
      loading: false,
      error: offerError,
      package: pkg,
      itemTotals,
      isPast,
      isSoldOut,
      isDeleted: deleted,
      isSavedForLater: saveForLater,
      isBookable,
      rate,
    }
  }

  return {
    offer,
    loaded: false,
    loading: offerLoading || ratesLoading,
    error: offerError,
    package: pkg,
    itemTotals: undefined,
    isPast,
    isSoldOut,
    isDeleted: deleted,
    isSavedForLater: saveForLater,
    isBookable,
    rate,
  }
}

export default useUserCartBedbankHotelItemData
